import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Flex,
  Input,
  Text,
  Textarea,
} from '@optiaxiom/react';

import type {
  AgentParameter,
  AgentParameterType,
  AgentParameterValue,
  EvaluationSample,
  EvaluationSampleCreate,
} from '@frontend/types';

import { ParameterForm } from '../../../shared/ParameterForm';

type EvaluationSampleFormProps = {
  initialValues?: EvaluationSample;
  agentParameters?: Array<AgentParameter<AgentParameterType>>;
  onSave: (values: EvaluationSampleCreate) => void;
  onCancel: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSaving?: boolean;
  onFileUpload?: (paramName: string, file: File) => Promise<void>;
};

export function EvaluationSampleForm({
  initialValues,
  agentParameters = [],
  onSave,
  onCancel,
  open,
  onOpenChange,
  isSaving = false,
  onFileUpload,
}: EvaluationSampleFormProps) {
  const editing = !!initialValues;
  const [sample, setSample] = useState<EvaluationSampleCreate>(
    initialValues ?? {
      name: '',
      output: '',
      input_parameters: null,
      description: null,
    }
  );
  const [uploadingParams, setUploadingParams] = useState<string[]>([]);

  // Reset form state when dialog opens or initialValues change
  useEffect(() => {
    if (open) {
      setSample(
        initialValues ?? {
          name: '',
          output: '',
          input_parameters: null,
          description: null,
        }
      );
      setUploadingParams([]);
    }
  }, [open, initialValues]);

  const handleParameterChange = (
    paramName: string,
    value: AgentParameterValue<AgentParameterType>
  ) => {
    setSample({
      ...sample,
      input_parameters: {
        ...(sample.input_parameters ?? {}),
        [paramName]: value,
      },
    });
  };

  const handleFileUploadWrapper = async (paramName: string, file: File) => {
    if (onFileUpload) {
      setUploadingParams(prev => [...prev, paramName]);
      try {
        await onFileUpload(paramName, file);
      } finally {
        setUploadingParams(prev => prev.filter(p => p !== paramName));
      }
    }
  };

  const handleSave = () => {
    if (!sample.name.trim() || !sample.output.trim()) {
      return;
    }

    onSave(sample);
  };

  const isSaveDisabled = !sample.name.trim() || !sample.output.trim() || isSaving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>{editing ? 'Edit Example' : 'Add Example'}</DialogHeader>
        <DialogBody>
          <Flex flexDirection="column" gap="16" w="full">
            <Field label="Name" required>
              <Input
                placeholder="Enter name"
                value={sample.name}
                h="40"
                onChange={e => setSample({ ...sample, name: e.target.value })}
              />
            </Field>

            {agentParameters.length > 0 && (
              <Flex flexDirection="column" gap="8">
                <Text fontSize="md" fontWeight="500">
                  Input Variables
                </Text>
                <ParameterForm
                  parameters={agentParameters}
                  values={sample.input_parameters ?? {}}
                  onChange={handleParameterChange}
                  onFileUpload={handleFileUploadWrapper}
                  uploadingParams={uploadingParams}
                />
              </Flex>
            )}

            <Field
              label="Output Example"
              required
              info="Provide an example output for Opal to evaluate this agent's response"
            >
              <Textarea
                placeholder="Enter your sample output"
                value={sample.output}
                onChange={e => setSample({ ...sample, output: e.target.value })}
                rows={6}
                resize="vertical"
              />
            </Field>
          </Flex>
        </DialogBody>
        <DialogFooter>
          <Flex
            flexDirection="row"
            gap="8"
            justifyContent="space-between"
            alignItems="center"
            w="full"
          >
            <Text color="fg.error" fontSize="sm">
              * Required fields
            </Text>
            <Flex flexDirection="row" gap="8">
              <Button appearance="default" onClick={onCancel} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSave}
                disabled={isSaveDisabled}
                loading={isSaving}
              >
                {editing ? 'Update' : 'Add'}
              </Button>
            </Flex>
          </Flex>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
